import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Heart, Calendar, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { DonationForm } from "@/components/DonationForm";
import { useSiteContent } from "@/hooks/useSiteContent";

const PrayerMinistry = () => {
  const { t } = useLanguage();
  const { content, isLoading } = useSiteContent('prayer_ministry');
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
              <Heart className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
              {content.title || "Prayer Ministry"}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {content.subtitle || "Interceding for our church, community, and the world"}
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-12">
            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{content.about_title || "About Prayer Ministry"}</h2>
              <p className="text-muted-foreground mb-4">
                {content.about_text1 || "Prayer is the foundation of our ministry. We believe in the power of prayer to transform lives and bring God's will to earth."}
              </p>
              <p className="text-muted-foreground">
                {content.about_text2 || "Join us in lifting up our church, community, and world through dedicated prayer and intercession."}
              </p>
            </section>

            {content.opportunities_title && (
              <section className="bg-card rounded-lg p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-foreground mb-6">{content.opportunities_title}</h2>
                <div className="space-y-6">
                  {content.opp1_title && (
                    <div className="border-l-4 border-primary pl-4">
                      <h3 className="font-bold text-xl mb-2">{content.opp1_title}</h3>
                      <div className="flex items-center gap-4 text-muted-foreground mb-2">
                        {content.opp1_day && (
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> {content.opp1_day}
                          </span>
                        )}
                        {content.opp1_time && (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {content.opp1_time}
                          </span>
                        )}
                      </div>
                      {content.opp1_desc && (
                        <p className="text-muted-foreground">{content.opp1_desc}</p>
                      )}
                    </div>
                  )}
                  {content.opp2_title && (
                    <div className="border-l-4 border-primary pl-4">
                      <h3 className="font-bold text-xl mb-2">{content.opp2_title}</h3>
                      <div className="flex items-center gap-4 text-muted-foreground mb-2">
                        {content.opp2_day && (
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> {content.opp2_day}
                          </span>
                        )}
                        {content.opp2_time && (
                          <span className="flex items-center gap-2">
                            <Clock className="w-4 h-4" /> {content.opp2_time}
                          </span>
                        )}
                      </div>
                      {content.opp2_desc && (
                        <p className="text-muted-foreground">{content.opp2_desc}</p>
                      )}
                    </div>
                  )}
                  {content.opp3_title && (
                    <div className="border-l-4 border-primary pl-4">
                      <h3 className="font-bold text-xl mb-2">{content.opp3_title}</h3>
                      <div className="flex items-center gap-4 text-muted-foreground mb-2">
                        {content.opp3_status && (
                          <span className="flex items-center gap-2">
                            <Users className="w-4 h-4" /> {content.opp3_status}
                          </span>
                        )}
                      </div>
                      {content.opp3_desc && (
                        <p className="text-muted-foreground">{content.opp3_desc}</p>
                      )}
                    </div>
                  )}
                  {content.opp4_title && (
                    <div className="border-l-4 border-primary pl-4">
                      <h3 className="font-bold text-xl mb-2">{content.opp4_title}</h3>
                      <div className="flex items-center gap-4 text-muted-foreground mb-2">
                        {content.opp4_status && (
                          <span className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" /> {content.opp4_status}
                          </span>
                        )}
                      </div>
                      {content.opp4_desc && (
                        <p className="text-muted-foreground">{content.opp4_desc}</p>
                      )}
                    </div>
                  )}
                </div>
              </section>
            )}

            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{content.submit_title || "Submit Prayer Requests"}</h2>
              <p className="text-muted-foreground mb-6">
                {content.submit_text || "Have a prayer need? We're here to pray with you. Your requests are kept confidential and lifted up by our prayer team."}
              </p>
              {(content.submit_way1 || content.submit_way2 || content.submit_way3) && (
                <div className="space-y-4" key={`${content.submit_way1}-${content.submit_way2}-${content.submit_way3}`}>
                  <p className="text-muted-foreground">
                    <strong>{content.submit_ways_heading || "Ways to submit a prayer request:"}</strong>
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    {content.submit_way1 && (
                      <li className="flex items-start gap-3">
                        <span className="text-primary font-bold">•</span>
                        <span>{content.submit_way1}</span>
                      </li>
                    )}
                    {content.submit_way2 && (
                      <li className="flex items-start gap-3">
                        <span className="text-primary font-bold">•</span>
                        <span>{content.submit_way2}</span>
                      </li>
                    )}
                    {content.submit_way3 && (
                      <li className="flex items-start gap-3">
                        <span className="text-primary font-bold">•</span>
                        <span>{content.submit_way3}</span>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </section>

            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">{content.join_title || "Join the Prayer Team"}</h2>
              <p className="text-muted-foreground mb-6">
                {content.join_text || "Feel called to intercede for others? We'd love for you to join our prayer team. Contact us to learn more about this vital ministry."}
              </p>
              <Button size="lg" onClick={() => window.location.href = "/#contact"}>
                {content.contact_button || "Contact Us"}
              </Button>
            </section>

            <section className="bg-card rounded-lg p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-foreground mb-6">Support Our Ministry</h2>
              <p className="text-muted-foreground mb-6">
                Your donations help maintain our prayer room and support our prayer ministry resources.
              </p>
              <DonationForm />
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrayerMinistry;
